import React, { createContext, useContext, useState, useCallback } from 'react';

// ============================================
// AI Panel Context
// ============================================
// Global state management for AI panels.
// Supports opening/closing/minimizing/maximizing panels.
// Persists across page navigation when placed in AppLayout.
// ============================================

// Panel types: 'main' | 'creator' | 'analytics' | 'document'
// Panel states: 'open' | 'minimized' | 'closed'

const AIPanelContext = createContext();

export function useAIPanel() {
    const ctx = useContext(AIPanelContext);
    if (!ctx) throw new Error('useAIPanel must be used within AIPanelProvider');
    return ctx;
}

export function AIPanelProvider({ children }) {
    // Map of panelId -> { type, state, props }
    const [panels, setPanels] = useState({});

    // Open a panel (or bring to foreground if already open)
    const openPanel = useCallback((panelId, type, props = {}) => {
        setPanels(prev => ({
            ...prev,
            [panelId]: {
                type,
                state: 'open',
                props,
                openedAt: Date.now(),
            },
        }));
    }, []);

    // Close a panel
    const closePanel = useCallback((panelId) => {
        setPanels(prev => {
            const next = { ...prev };
            delete next[panelId];
            return next;
        });
    }, []);

    // Minimize a panel (dock to bottom bar)
    const minimizePanel = useCallback((panelId) => {
        setPanels(prev => {
            if (!prev[panelId]) return prev;
            return {
                ...prev,
                [panelId]: { ...prev[panelId], state: 'minimized' },
            };
        });
    }, []);

    // Maximize / restore a panel from minimized
    const restorePanel = useCallback((panelId) => {
        setPanels(prev => {
            if (!prev[panelId]) return prev;
            return {
                ...prev,
                [panelId]: { ...prev[panelId], state: 'open' },
            };
        });
    }, []);

    // Update panel props (e.g., pass new prefilledData)
    const updatePanelProps = useCallback((panelId, newProps) => {
        setPanels(prev => {
            if (!prev[panelId]) return prev;
            return {
                ...prev,
                [panelId]: { ...prev[panelId], props: { ...prev[panelId].props, ...newProps } },
            };
        });
    }, []);

    // Convenience helpers
    const openMainAI = useCallback(() => openPanel('main', 'main'), [openPanel]);

    const openCreator = useCallback((creatorType, prefilledData = {}) => {
        openPanel('creator', 'creator', { creatorType, prefilledData });
    }, [openPanel]);

    const openAnalytics = useCallback((initialQuestion = null) => {
        openPanel('analytics', 'analytics', { initialQuestion });
    }, [openPanel]);

    const openDocumentAI = useCallback((file, allFiles = []) => {
        openPanel('document', 'document', { file, allFiles });
    }, [openPanel]);

    // Get active (open) panels
    const activePanels = Object.entries(panels)
        .filter(([, p]) => p.state === 'open')
        .map(([id, p]) => ({ id, ...p }));

    // Get minimized panels
    const minimizedPanels = Object.entries(panels)
        .filter(([, p]) => p.state === 'minimized')
        .map(([id, p]) => ({ id, ...p }));

    // Check if a panel is open
    const isPanelOpen = useCallback((panelId) => panels[panelId]?.state === 'open', [panels]);

    const value = {
        panels,
        activePanels,
        minimizedPanels,
        openPanel,
        closePanel,
        minimizePanel,
        restorePanel,
        updatePanelProps,
        isPanelOpen,
        // Convenience
        openMainAI,
        openCreator,
        openAnalytics,
        openDocumentAI,
    };

    return (
        <AIPanelContext.Provider value={value}>
            {children}
        </AIPanelContext.Provider>
    );
}

export default AIPanelContext;
